import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import {
  useUserHasFeature,
  useUserHasVenueMapSvgVersionFallbackFeature,
} from 'src/hooks/useUserHasFeature';
import { ReportMapIssueButton } from 'src/modals/VenueMapReportModal';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, SectionInfo } from 'src/WebApiController';

import { useCtrlPlusM, useVenueMapSvgFormat } from './EventVenueMap.hooks';
import { VenueMapV3 } from './v3/VenueMapV3';
import { VenueMap } from './VenueMap';
import * as styles from './VenueMap.css';
import { VenueMapProps } from './VenueMapProps';

export type EventVenueMapProps = Omit<
  VenueMapProps,
  | 'selectedSections'
  | 'svgMapUrl'
  | 'venueConfigId'
  | 'availableSections'
  | 'focalPoint'
> & {
  selectedSectionIds?: number[];
  onToggleMirrors?: (sections: SectionInfo[], exclude?: boolean) => void;
  /**
   * Show the "report map issue" action overlay (POS-9306). Off by default so it
   * only appears where intended (the inventory event MapBody and the create-PO
   * modal map), not on every venue map surface.
   */
  showReportMapIssue?: boolean;
};

export const EventVenueMap = ({
  selectedSectionIds,
  markedSectionIds,
  animatedSectionIds,
  onSectionClicked,
  onSectionHovered,
  onSectionTapAndHold,
  setSelectedSections,
  onToggleMirrors,
  staticInfoContent,
  staticInfoContentPosition,
  alwaysShowStaticInfo,
  infoIconContent,
  isZoomEnabled = true,
  hideControls = false,
  isPanEnabled = true,
  showDefaultMapColors,
  colorBandProps,
  getColor,
  useRowMap = false,
  isHeatMap = false,
  isWhiteBackground = false,
  isRelocatedStaticInfo = false,
  allowDrawLine = true,
  isCondensedView = false,
  cornerContent,
  statsContent,
  disableHoverEffect = false,
  mapActionContainerClassname,
  condensedControl = false,
  showReportMapIssue = false,
}: EventVenueMapProps) => {
  const { venueMapInfo, event, isMapLoading, isGeneralAdmissionOnly } =
    useEventMapContext();
  const hasCatalogSupportFeature = useUserHasFeature(Feature.CatalogSupport);
  const hasVenueMapSvgVersionFallback =
    useUserHasVenueMapSvgVersionFallbackFeature();

  useCtrlPlusM(markedSectionIds ?? selectedSectionIds ?? [], onToggleMirrors);

  const {
    format: venueMapSvgFormat,
    v3Url,
    v2Url,
    isLoading: isSvgFormatLoading,
  } = useVenueMapSvgFormat(
    venueMapInfo?.venueCfgId,
    venueMapInfo?.svgMapVer,
    hasVenueMapSvgVersionFallback
  );

  const selectedSectionData = useMemo(() => {
    if (!venueMapInfo || !selectedSectionIds?.length) {
      return undefined;
    }

    return venueMapInfo.sections?.filter((s: SectionInfo) =>
      selectedSectionIds.includes(s.id)
    );
  }, [selectedSectionIds, venueMapInfo]);

  // POS-9606: when the fallback feature is on, pick the renderer by probing the
  // actual V3 SVG asset (and falling back to V2) instead of the geoJson-derived
  // hasRow flag, which can be out of sync with the CDN. Off = previous behaviour.
  const shouldUseV3 = hasVenueMapSvgVersionFallback
    ? venueMapSvgFormat === 'v3'
    : venueMapInfo?.hasRow;

  const svgMapUrl = shouldUseV3 ? v3Url : v2Url;

  if (isMapLoading || isSvgFormatLoading) {
    return (
      <div className={styles.venueMapUnavailableContainer}>
        <PosSpinner />
      </div>
    );
  }
  if (!venueMapInfo) {
    return (
      <div className={styles.venueMapUnavailableContainer}>
        <Content id={ContentId.MapUnavailable} />
      </div>
    );
  }

  return (
    <div className={styles.eventVenueMapContainer}>
      {shouldUseV3 ? (
        <VenueMapV3
          svgMapUrl={svgMapUrl}
          useRowMap={useRowMap}
          venueConfigId={venueMapInfo.venueCfgId}
          focalPoint={venueMapInfo.focalPt}
          availableSections={venueMapInfo.sections ?? []}
          selectedSections={selectedSectionData}
          markedSectionIds={markedSectionIds}
          animatedSectionIds={animatedSectionIds}
          onSectionClicked={onSectionClicked}
          onSectionTapAndHold={onSectionTapAndHold}
          onSectionHovered={onSectionHovered}
          setSelectedSections={setSelectedSections}
          staticInfoContent={staticInfoContent}
          staticInfoContentPosition={staticInfoContentPosition}
          alwaysShowStaticInfo={alwaysShowStaticInfo}
          infoIconContent={infoIconContent}
          isGeneralAdmissionOnly={isGeneralAdmissionOnly}
          showDefaultMapColors={showDefaultMapColors}
          colorBandProps={colorBandProps}
          isZoomEnabled={isZoomEnabled}
          hideControls={hideControls}
          isPanEnabled={isPanEnabled}
          isWhiteBackground={isWhiteBackground}
          isRelocatedStaticInfo={isRelocatedStaticInfo}
          getColor={getColor}
          isHeatMap={isHeatMap}
          cornerContent={cornerContent}
          statsContent={statsContent}
          allowDrawLine={allowDrawLine}
          disableHoverEffect={disableHoverEffect}
          isCondensedView={isCondensedView}
          mapActionContainerClassname={mapActionContainerClassname}
          condensedControl={condensedControl}
        />
      ) : (
        <VenueMap
          svgMapUrl={svgMapUrl}
          venueConfigId={venueMapInfo.venueCfgId}
          focalPoint={venueMapInfo.focalPt}
          availableSections={venueMapInfo.sections ?? []}
          selectedSections={selectedSectionData}
          markedSectionIds={markedSectionIds}
          animatedSectionIds={animatedSectionIds}
          onSectionClicked={onSectionClicked}
          onSectionTapAndHold={onSectionTapAndHold}
          onSectionHovered={onSectionHovered}
          setSelectedSections={setSelectedSections}
          staticInfoContent={staticInfoContent}
          staticInfoContentPosition={staticInfoContentPosition}
          infoIconContent={infoIconContent}
          isGeneralAdmissionOnly={isGeneralAdmissionOnly}
          showDefaultMapColors={showDefaultMapColors}
          colorBandProps={colorBandProps}
          isZoomEnabled={isZoomEnabled}
          hideControls={hideControls}
          isPanEnabled={isPanEnabled}
          isWhiteBackground={isWhiteBackground}
          isRelocatedStaticInfo={isRelocatedStaticInfo}
          isCondensedView={isCondensedView}
          mapActionContainerClassname={mapActionContainerClassname}
          getColor={getColor}
          isHeatMap={isHeatMap}
          cornerContent={cornerContent}
          statsContent={statsContent}
          allowDrawLine={allowDrawLine}
          disableHoverEffect={disableHoverEffect}
          condensedControl={condensedControl}
        />
      )}
      {/* Report-map-issue action, overlaid like the other map action icons.
          Only shown where opted in (inventory event MapBody and the create-PO
          modal map) and behind the catalog-support feature — POS-9306. The
          button further self-gates on the inventory-edit permission. */}
      {showReportMapIssue && hasCatalogSupportFeature && (
        <div className={styles.reportMapIssueOverlay}>
          <ReportMapIssueButton
            eventId={event?.viagId}
            venueConfigId={venueMapInfo.venueCfgId}
          />
        </div>
      )}
    </div>
  );
};
